import React, { useState, useEffect } from 'react';
import '../styles/Homepage.css';
import ModernFeaturesSection from '../common/ModernFeaturesSection';
import ImpactNumbers from '../common/ImpactNumbers';
import Testimonials from '../common/Testimonials';
import AvailableBikes from '../common/AvailableBikes';
import termsPDF from '../agreement/Beaver_Tours_EV_Partnership_Agreement.pdf';

function Homepage() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        condition: '',
        brand: '',
        model: '',
        year: '',
        kms: '',
        vehicleImages: [],
        vehicleFiles: [],
        aadharFront: null,
        aadharFrontFile: null,
        aadharBack: null,
        aadharBackFile: null,
        rcFront: null,
        rcFrontFile: null,
        rcBack: null,
        rcBackFile: null,
        agreeToTerms: false
    });
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    const handleInputChange = (e) => {
        const input = e.target;
        if (input.name === 'vehicleImages') {
            if (input.files.length === 0) {
                input.value = '';
                return;
            }
            const files = Array.from(input.files);
            const validFiles = files.filter(file => {
                if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
                    setAlert({ show: true, message: 'Please upload only JPEG or PNG images.', type: 'error' });
                    return false;
                }
                if (file.size > 5 * 1024 * 1024) {
                    setAlert({ show: true, message: `${file.name} is too large. Images must be less than 5MB.`, type: 'error' });
                    return false;
                }
                return true;
            });

            if (formData.vehicleFiles.length + validFiles.length > 5) {
                setAlert({ show: true, message: 'Maximum 5 vehicle images allowed.', type: 'error' });
                input.value = '';
                return;
            }

            Promise.all(validFiles.map(file => {
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(file);
                });
            })).then(results => {
                setFormData({
                    ...formData,
                    vehicleImages: [...formData.vehicleImages, ...results],
                    vehicleFiles: [...formData.vehicleFiles, ...validFiles]
                });
                input.value = '';
            });
        } else if (input.name === 'aadharFront' || input.name === 'aadharBack' || input.name === 'rcFront' || input.name === 'rcBack') {
            if (input.files.length === 0) {
                setFormData({
                    ...formData,
                    [input.name]: null,
                    [`${input.name}File`]: null
                });
                input.value = '';
                return;
            }
            const file = input.files[0];
            if (file) {
                if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
                    setAlert({ show: true, message: 'Please upload only JPEG or PNG images.', type: 'error' });
                    input.value = '';
                    return;
                }
                if (file.size > 5 * 1024 * 1024) {
                    setAlert({ show: true, message: 'Image must be less than 5MB.', type: 'error' });
                    input.value = '';
                    return;
                }
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFormData({
                        ...formData,
                        [input.name]: reader.result,
                        [`${input.name}File`]: file
                    });
                    input.value = '';
                };
                reader.readAsDataURL(file);
            }
        } else if (input.name === 'agreeToTerms') {
            setFormData({ ...formData, agreeToTerms: input.checked });
        } else {
            setFormData({ ...formData, [input.name]: input.value });
        }
    };

    const removeImage = (index, type) => {
        if (type === 'vehicle') {
            const newImages = formData.vehicleImages.filter((_, i) => i !== index);
            const newFiles = formData.vehicleFiles.filter((_, i) => i !== index);
            setFormData({ ...formData, vehicleImages: newImages, vehicleFiles: newFiles });
        } else if (type === 'aadharFront') {
            setFormData({ ...formData, aadharFront: null, aadharFrontFile: null });
        } else if (type === 'aadharBack') {
            setFormData({ ...formData, aadharBack: null, aadharBackFile: null });
        } else if (type === 'rcFront') {
            setFormData({ ...formData, rcFront: null, rcFrontFile: null });
        } else if (type === 'rcBack') {
            setFormData({ ...formData, rcBack: null, rcBackFile: null });
        }
    };

    const handleTermsPDFDownload = () => {
        const link = document.createElement('a');
        link.href = termsPDF;
        link.download = 'terms-and-conditions.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.agreeToTerms) {
            setAlert({ show: true, message: 'You must agree to the terms and conditions.', type: 'error' });
            return;
        }
        if (!formData.aadharFrontFile || !formData.aadharBackFile || !formData.rcFrontFile || !formData.rcBackFile) {
            setAlert({ show: true, message: 'Please upload all required images (Aadhar and RC front/back).', type: 'error' });
            return;
        }
        if (formData.vehicleFiles.length === 0) {
            setAlert({ show: true, message: 'Please upload at least one vehicle image.', type: 'error' });
            return;
        }
        setIsLoading(true);

        const whatsappMessage = encodeURIComponent(
            `New Vehicle Attachment Request\n\n` +
            `Name: ${formData.name}\n` +
            `Address: ${formData.address}\n` +
            `Phone: ${formData.phone}\n` +
            `Email: ${formData.email || 'Not provided'}\n` +
            `Vehicle Condition: ${formData.condition}\n` +
            `Brand: ${formData.brand}\n` +
            `Model: ${formData.model}\n` +
            `Year: ${formData.year}\n` +
            `KMs Run: ${formData.kms}\n` +
            `Agreed to Terms: ${formData.agreeToTerms ? 'Yes' : 'No'}\n` +
            `Vehicle Images: ${formData.vehicleFiles.length} image(s) uploaded\n` +
            `Aadhar Card: Front and back images uploaded\n` +
            `RC: Front and back images uploaded`
        );
        const whatsappUrl = `https://wa.me/+918667693063?text=${whatsappMessage}`;
        console.log('WhatsApp URL:', whatsappUrl);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('address', formData.address);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('email', formData.email || '');
            formDataToSend.append('condition', formData.condition);
            formDataToSend.append('brand', formData.brand);
            formDataToSend.append('model', formData.model);
            formDataToSend.append('year', formData.year);
            formDataToSend.append('kms', formData.kms);
            formDataToSend.append('agreeToTerms', String(formData.agreeToTerms));

            formData.vehicleFiles.forEach(file => {
                formDataToSend.append('vehicleImages', file);
            });
            formDataToSend.append('aadharFront', formData.aadharFrontFile);
            formDataToSend.append('aadharBack', formData.aadharBackFile);
            formDataToSend.append('rcFront', formData.rcFrontFile);
            formDataToSend.append('rcBack', formData.rcBackFile);

            const apiUrl = `${process.env.REACT_APP_API_URL}/api/attach-vehicle`;
            console.log('API URL:', apiUrl);

            const response = await fetch(apiUrl, {
                method: 'POST',
                body: formDataToSend
            });
            console.log('Response Status:', response.status);

            const responseData = await response.json();
            console.log('Response Data:', responseData);

            if (response.ok) {
                setIsModalOpen(false);
                setFormData({
                    name: '',
                    address: '',
                    phone: '',
                    email: '',
                    condition: '',
                    brand: '',
                    model: '',
                    year: '',
                    kms: '',
                    vehicleImages: [],
                    vehicleFiles: [],
                    aadharFront: null,
                    aadharFrontFile: null,
                    aadharBack: null,
                    aadharBackFile: null,
                    rcFront: null,
                    rcFrontFile: null,
                    rcBack: null,
                    rcBackFile: null,
                    agreeToTerms: false
                });
                setAlert({ show: true, message: 'Vehicle attachment request submitted successfully!', type: 'success' });
                setTimeout(() => {
                    window.location.href = whatsappUrl;
                }, 1000);
            } else {
                console.error('Backend error:', responseData.message);
                setAlert({ show: true, message: responseData.message || 'Failed to submit form. Please try again.', type: 'error' });
            }
        } catch (error) {
            console.error('Submission error:', error);
            setAlert({ show: true, message: 'Server error. Please try again later.', type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (alert.show) {
            const timer = setTimeout(() => {
                setAlert({ show: false, message: '', type: '' });
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [alert.show]);

    return (
        <main className="homepage-main">
            {alert.show && (
                <div className={`alert alert-${alert.type}`}>
                    {alert.message}
                </div>
            )}

            <section className="hero-section">
                <div className="hero-content">
                    <h1 className="hero-title">Welcome to Beaver Tours</h1>
                    <p className="hero-subtitle">Explore the city with our premium bike rentals!</p>
                    <div className="hero-buttons">
                        <a href="/book" className="hero-button hover-scale">Book Now</a>
                        <button
                            className="hero-button hover-scale attach-vehicle"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Attach Your Vehicle
                        </button>
                    </div>
                </div>
            </section>

            <ModernFeaturesSection />
            <ImpactNumbers />
            <AvailableBikes />

            <section className="cta-section">
                <div className="cta-container">
                    <h2 className="cta-title">Ready for Your Next Adventure?</h2>
                    <p className="cta-text">
                        Book your bike today and explore the city at your own pace.
                    </p>
                    <div className="cta-buttons">
                        <a href="/book" className="cta-button primary">Book Now</a>
                        <a href="/contact" className="cta-button secondary">Contact Us</a>
                    </div>
                </div>
            </section>
            <Testimonials />

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button
                            className="modal-close"
                            onClick={() => setIsModalOpen(false)}
                        >
                            ×
                        </button>
                        <h2 className="modal-title">Attach Your Vehicle</h2>
                        <form onSubmit={handleSubmit} className="vehicle-form">
                            <h3>Personal Details</h3>
                            <input
                                type="text"
                                name="name"
                                placeholder="Full Name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                            <input
                                type="text"
                                name="address"
                                placeholder="Address"
                                value={formData.address}
                                onChange={handleInputChange}
                                required
                            />
                            <input
                                type="tel"
                                name="phone"
                                placeholder="Phone Number"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                            />
                            <input
                                type="email"
                                name="email"
                                placeholder="Email (Optional)"
                                value={formData.email}
                                onChange={handleInputChange}
                            />

                            <h3>Vehicle Details</h3>
                            <select
                                name="condition"
                                value={formData.condition}
                                onChange={handleInputChange}
                                required
                                className="vehicle-form-select"
                            >
                                <option value="" disabled>Select Vehicle Condition</option>
                                <option value="Excellent">Excellent</option>
                                <option value="Good">Good</option>
                                <option value="Average">Average</option>
                            </select>
                            <input
                                type="text"
                                name="brand"
                                placeholder="Brand"
                                value={formData.brand}
                                onChange={handleInputChange}
                                required
                            />
                            <input
                                type="text"
                                name="model"
                                placeholder="Model"
                                value={formData.model}
                                onChange={handleInputChange}
                                required
                            />
                            <input
                                type="number"
                                name="year"
                                placeholder="Year of Registration"
                                value={formData.year}
                                onChange={handleInputChange}
                                required
                            />
                            <input
                                type="number"
                                name="kms"
                                placeholder="KMs Run"
                                value={formData.kms}
                                onChange={handleInputChange}
                                required
                            />
                            <div className="file-upload-section">
                                <label>Vehicle Images (up to 5, JPEG or PNG)</label>
                                <input
                                    type="file"
                                    name="vehicleImages"
                                    accept="image/jpeg,image/jpg,image/png"
                                    multiple
                                    onChange={handleInputChange}
                                />
                                <div className="image-preview-container">
                                    {formData.vehicleImages.map((image, index) => (
                                        <div key={index} className="image-preview">
                                            <img src={image} alt={`Vehicle ${index + 1}`} />
                                            <button
                                                type="button"
                                                className="remove-image"
                                                onClick={() => removeImage(index, 'vehicle')}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="file-upload-section">
                                <label>Aadhar Card - Front (JPEG or PNG)</label>
                                <input
                                    type="file"
                                    name="aadharFront"
                                    accept="image/jpeg,image/jpg,image/png"
                                    onChange={handleInputChange}
                                />
                                {formData.aadharFront && (
                                    <div className="image-preview-container">
                                        <div className="image-preview">
                                            <img src={formData.aadharFront} alt="Aadhar Front" />
                                            <button
                                                type="button"
                                                className="remove-image"
                                                onClick={() => removeImage(0, 'aadharFront')}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="file-upload-section">
                                <label>Aadhar Card - Back (JPEG or PNG)</label>
                                <input
                                    type="file"
                                    name="aadharBack"
                                    accept="image/jpeg,image/jpg,image/png"
                                    onChange={handleInputChange}
                                />
                                {formData.aadharBack && (
                                    <div className="image-preview-container">
                                        <div className="image-preview">
                                            <img src={formData.aadharBack} alt="Aadhar Back" />
                                            <button
                                                type="button"
                                                className="remove-image"
                                                onClick={() => removeImage(0, 'aadharBack')}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="file-upload-section">
                                <label>Vehicle RC - Front (JPEG or PNG)</label>
                                <input
                                    type="file"
                                    name="rcFront"
                                    accept="image/jpeg,image/jpg,image/png"
                                    onChange={handleInputChange}
                                />
                                {formData.rcFront && (
                                    <div className="image-preview-container">
                                        <div className="image-preview">
                                            <img src={formData.rcFront} alt="RC Front" />
                                            <button
                                                type="button"
                                                className="remove-image"
                                                onClick={() => removeImage(0, 'rcFront')}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="file-upload-section">
                                <label>Vehicle RC - Back (JPEG or PNG)</label>
                                <input
                                    type="file"
                                    name="rcBack"
                                    accept="image/jpeg,image/jpg,image/png"
                                    onChange={handleInputChange}
                                />
                                {formData.rcBack && (
                                    <div className="image-preview-container">
                                        <div className="image-preview">
                                            <img src={formData.rcBack} alt="RC Back" />
                                            <button
                                                type="button"
                                                className="remove-image"
                                                onClick={() => removeImage(0, 'rcBack')}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="terms-section">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="agreeToTerms"
                                        checked={formData.agreeToTerms}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    I agree to the{' '}
                                    <button
                                        type="button"
                                        className="terms-link"
                                        onClick={handleTermsPDFDownload}
                                    >
                                        Terms and Conditions
                                    </button>
                                </label>
                            </div>
                            <button
                                type="submit"
                                className="submit-button"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Submitting...' : 'Submit'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-spinner"></div>
                    <p>Submitting your request...</p>
                </div>
            )}
        </main>
    );
}

export default Homepage;